import _ from "lodash";
import React, { useCallback, useEffect, useId, useReducer } from "react";

// DS
import {
  Modal,
  TextInput,
  ColorPicker,
  IconButton,
  Button,
  EmptyState,
  FormInputError,
} from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  AddCircleOutlined,
  AddOutlined,
} from "@toddle-edu/ds-icons";

// css
import classes from "./ManageFoldersModal.scss";

// Utilites
import { useI18n } from "Hooks";
import {
  COLORS_MAPPING_FOR_CHIP,
  folderManagementReducer,
  bulkCourseFoldersActions,
} from "AppComponents/Post/modules/PostModule";
import { generateRandomId } from "Utils";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { NoFoldersIllustration } from "@toddle-edu/ds-theme";
import { isPortfolioFilterRevamp } from "../../utils";
import classNames from "classnames";

const BLANK_OBJECT = {};
const style = {
  textInputWidthV2: {
    width: "330px",
  },
  textInputWidth: {
    width: "276px",
  },
};
const colorPalette = _.uniqBy(
  _.map(_.keys(COLORS_MAPPING_FOR_CHIP), color => ({
    color: COLORS_MAPPING_FOR_CHIP[color],
  })),
  "color"
);

// Function which gives unique folder name out of all the folders
const getUniqueFolderName = folders => {
  const folderNames = _.map(folders, folder => folder?.name);
  let folderName = "Untitled";
  let folderNameIndex = 1;
  while (_.includes(folderNames, folderName)) {
    folderName = `Untitled ${folderNameIndex}`;
    folderNameIndex++;
  }
  return folderName;
};

// Memoized function to check if folder name already exists
const checkFolderExistsMemoized = _.memoize(
  ({ folderName, folders }) => {
    const folderNames = _.map(folders, folder => folder?.name?.toLowerCase());
    return _.includes(folderNames, folderName?.toLowerCase());
  },
  ({ folderName, folders }) => {
    const cacheData = {
      folderName: folderName?.toLowerCase(),
      folderIds: folders.map(folder => folder?.id),
    };
    return JSON.stringify(cacheData);
  }
);

const ManageFoldersModal = props => {
  const { t } = useI18n(["journal", "common"]);
  const folderErrorId = useId();
  const {
    courseFolders,
    bulkCourseFoldersActions,
    createFolderOnModalOpen,
    setCreateFolderOnModalOpen,
    courseIdFromParent,
    closeCreateFolderOnModalOpen = true,
  } = props;
  const [foldersWithError, setFoldersWithError] = React.useState([]);
  const [showFullScreenLoader, setShowFullScreenLoader] = React.useState(false);
  const [foldersData, dispatch] = useReducer(folderManagementReducer, {
    folders: courseFolders,
  });

  const borderStyles = { borderTop: "1px solid var(--border-secondary)" };

  useEffect(() => {
    if (!_.isEqual(foldersData?.folders, courseFolders))
      dispatch({ type: "SET_FOLDERS", payload: { folders: courseFolders } });
  }, [courseFolders]);

  useEffect(() => {
    if (createFolderOnModalOpen) {
      onAddFolderClick();
      closeCreateFolderOnModalOpen && setCreateFolderOnModalOpen(false);
    }
  }, [createFolderOnModalOpen]);

  const onAddFolderClick = () => {
    const folderToAdd = {
      id: "NEW_FOLDER_" + generateRandomId(),
      name: getUniqueFolderName(foldersData?.folders),
      color: COLORS_MAPPING_FOR_CHIP[_.sample(_.keys(COLORS_MAPPING_FOR_CHIP))],
    };
    dispatch({ type: "ADD_NEW_FOLDER", payload: { folderToAdd } });
  };

  const onRemoveFolderClick = folderId => {
    if (!_.isEmpty(folderId))
      dispatch({ type: "REMOVE_FOLDER", payload: { folderId } });
  };

  // Debounced validation function
  const debouncedValidation = useCallback(
    _.debounce((folderName, folderId) => {
      const exists = checkFolderExistsMemoized({
        folderName,
        folders: foldersData?.folders,
      });

      if (exists) {
        setFoldersWithError(prev => [...prev, folderId]);
      } else {
        setFoldersWithError(prev => _.filter(prev, id => id !== folderId));
      }
    }, 300),
    [foldersData?.folders]
  );

  useEffect(() => {
    return () => {
      debouncedValidation.cancel();
    };
  }, []);

  const updateFolderName = React.useCallback(
    (folderName, oldFolder) => {
      if (!_.isEmpty(oldFolder?.id)) {
        dispatch({
          type: "UPDATE_FOLDER",
          payload: {
            updatedFolder: {
              ...oldFolder,
              name: folderName,
            },
          },
        });

        setFoldersWithError(prev => _.filter(prev, id => id !== oldFolder.id));

        debouncedValidation(folderName, oldFolder.id);
      }
    },
    [debouncedValidation]
  );

  const handleFolderNameChange = useCallback(
    folder => e => {
      updateFolderName(e.target.value, folder);
    },
    [updateFolderName]
  );

  const updateFolderColor = (folderColor, oldFolder) => {
    if (!_.isEmpty(oldFolder?.id))
      dispatch({
        type: "UPDATE_FOLDER",
        payload: {
          updatedFolder: {
            ...oldFolder,
            color: folderColor,
          },
        },
      });
  };

  const onDoneClick = async ({ isLastFolderDeleted = false }) => {
    await bulkCourseFoldersActions({
      existingFolders: courseFolders,
      folders: isLastFolderDeleted ? [] : foldersData?.folders,
      setShowFullScreenLoader,
      onSuccessCallback: () => props.onClose?.(),
      courseIdFromParent,
    });
  };

  const onCancelClick = () => {
    props.onClose?.();
    dispatch({ type: "SET_FOLDERS", payload: { folders: courseFolders } });
    setFoldersWithError([]);
  };

  const isFolderNameEmpty = _.some(foldersData?.folders, folder =>
    _.isEmpty(folder?.name)
  );

  const emptyContainerClasses = classNames({
    [classes.emptyContainer]: !isPortfolioFilterRevamp(),
    [classes.emptyContainerV2]: isPortfolioFilterRevamp(),
  });

  const selectorClasses = classNames({
    [classes.selectorContainers]: !isPortfolioFilterRevamp(),
    [classes.selectorContainersV2]: isPortfolioFilterRevamp(),
  });

  const onColorChange = folder => colorPalatteObj => {
    updateFolderColor(colorPalatteObj?.color, folder);
  };

  const handleRemoveFolderClick = folderId => () => {
    onRemoveFolderClick(folderId);
    if (_.size(foldersData?.folders) === 1) {
      onDoneClick({ isLastFolderDeleted: true });
    }
  };

  return (
    <>
      <Modal
        dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
        width="480px"
        height={isPortfolioFilterRevamp() ? "456px" : "600px"}
        onClose={onCancelClick}
        isOpen={props.isOpen}
      >
        <Modal.Header title={t("journal:manage_folders")} />
        <Modal.Body
          isScrollable={true}
          style={
            isPortfolioFilterRevamp() && !(_.size(foldersData?.folders) > 0)
              ? {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
              : BLANK_OBJECT
          }
        >
          {_.size(foldersData?.folders) > 0 ? (
            <div className={classes.foldersListContainer}>
              {_.map(foldersData?.folders, (folder, index) => (
                <div>
                  <div className={classes.folderItem} key={folder?.id}>
                    <TextInput
                      dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                      size={isPortfolioFilterRevamp() ? "medium" : "large"}
                      value={folder?.name}
                      error={
                        _.includes(foldersWithError, folder?.id) ||
                        !_.size(folder?.name)
                      }
                      onChange={handleFolderNameChange(folder)}
                      style={
                        isPortfolioFilterRevamp()
                          ? style.textInputWidthV2
                          : style.textInputWidth
                      }
                      autoFocus={index === 0}
                      testId="journal-feed-foldername"
                      aria-describedby={`${folderErrorId}-${index}`}
                    />
                    <div className={selectorClasses}>
                      <ColorPicker
                        dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                        type={isPortfolioFilterRevamp() ? "plain" : undefined}
                        isCompact={isPortfolioFilterRevamp()}
                        colorPalette={colorPalette}
                        selectedColor={COLORS_MAPPING_FOR_CHIP[folder?.color]}
                        onColorChange={onColorChange(folder)}
                        variant="outlined-subtle"
                        size={isPortfolioFilterRevamp() ? "medium" : "large"}
                        testId="journal-feed-foldercolor"
                      />
                      <IconButton
                        dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                        variant={
                          isPortfolioFilterRevamp()
                            ? "neutral"
                            : "outlined-subtle"
                        }
                        size={isPortfolioFilterRevamp() ? "medium" : "large"}
                        icon={<DeleteOutlined />}
                        onClick={handleRemoveFolderClick(folder?.id)}
                        testId="journal-feed-deletefolder"
                      />
                    </div>
                  </div>
                  <FormInputError
                    id={`${folderErrorId}-${index}`}
                    {...((_.includes(foldersWithError, folder?.id) ||
                      !_.size(folder?.name)) && {
                      error: _.size(folder?.name)
                        ? t("journal:folder_already_exists")
                        : t("journal:folder_name_empty"),
                      className: classes.errorClass,
                    })}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={emptyContainerClasses}>
              <EmptyState
                illustration={NoFoldersIllustration}
                title={t("journal:folder_created_will_appear_here")}
                subtitle={t("journal:click_to_create_first_folder")}
                primaryButton={
                  isPortfolioFilterRevamp() ? (
                    <Button
                      dsVersion="2.0"
                      icon={<AddOutlined variant="subtle" />}
                      type={"outlined"}
                      variant={"neutral"}
                      size={"medium"}
                      onClick={onAddFolderClick}
                    >
                      {t("common:create_new_folder")}
                    </Button>
                  ) : null
                }
              />
            </div>
          )}
        </Modal.Body>
        {(isPortfolioFilterRevamp()
          ? _.size(foldersData?.folders) > 0
          : true) && (
          <Modal.Footer
            hasSeperator={true}
            style={isPortfolioFilterRevamp() ? borderStyles : BLANK_OBJECT}
          >
            <div className={classes.modalFooterContainer}>
              <Button
                dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                type="inline"
                variant={
                  isPortfolioFilterRevamp()
                    ? "progressive"
                    : "inline-progressive"
                }
                size={isPortfolioFilterRevamp() ? "medium" : "large"}
                icon={<AddCircleOutlined />}
                onClick={onAddFolderClick}
                testId="journal-feed-createfolder"
              >
                {t("common:create_new_folder")}
              </Button>
              <div className={classes.modalFooterButtonsContainer}>
                <Button
                  dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                  type={isPortfolioFilterRevamp() ? "outlined" : undefined}
                  variant={
                    isPortfolioFilterRevamp() ? "neutral" : "outlined-subtle"
                  }
                  size={isPortfolioFilterRevamp() ? "medium" : "large"}
                  onClick={onCancelClick}
                  testId="journal-feed-cancelfolder"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  dsVersion={isPortfolioFilterRevamp() ? "2.0" : "1.0"}
                  variant={
                    isPortfolioFilterRevamp() ? "primary" : "progressive"
                  }
                  size={isPortfolioFilterRevamp() ? "medium" : "large"}
                  onClick={onDoneClick}
                  disabled={isFolderNameEmpty || _.size(foldersWithError)}
                  testId="journal-feed-donefolder"
                >
                  {t("common:done")}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        )}
      </Modal>
      {showFullScreenLoader ? (
        <FullScreenLoader overlayContainer={classes.loadingOverlayContainer} />
      ) : null}
    </>
  );
};

const mapStateToProps = () => {
  return BLANK_OBJECT;
};

const mapActionCreators = {
  bulkCourseFoldersActions,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  ManageFoldersModal
);
